import React, { useEffect, useState, useRef } from 'react';
import { styled } from '@glitz/react';
import SwiperList from 'Shared/SwiperList';
import { Favourite24x24, FavouriteFull24x24 } from 'Shared/Icons/Favorite';
import { Plus24x24 } from 'Shared/Icons/Plus';
import TagViewModel from 'Shared/ViewModels/TagViewModel.type';
import * as style from 'Shared/Style';
import { OverlayNotABackdrop } from 'Shared/SharedComponents/atoms/AspectRatio';
import { BgTag } from 'Product/ProductTags';
import { colorFromTag } from 'Shared/utils';
import Resource from '../../../VoyadoElevate/Models/Resource.type';
import Video from 'Shared/Video/Video';
import VideoSource from 'Shared/ViewModels/VideoSourceViewModel.type';
import { PictureImage, Preset } from 'Shared/Image';

const FavoriteIcon = styled(Favourite24x24, {
})

const FavoriteFullIcon = styled(FavouriteFull24x24, {
  color: style.colors.monochrome.black
})

const Wrap = styled.div({
  position: 'relative', width: '100%'
})

const ShowZoomWrap = styled.div({
  display: 'flex',
  position: 'fixed',
  zIndex: 1000,
  top: '0',
  left: 0,
  pointerEvents: 'none',
  height: '100vh',
  width: '100vw',
  padding: { xy: '16px' },
  justifyContent: 'flex-end',
})

const ShowZoomIcon = styled(Plus24x24, {
  pointerEvents: 'all',
  transform: 'rotate(45deg)',
  color: style.colors.monochrome.black,
  marginRight: '12px',
  cursor: 'pointer',
})

const ZoomContainer = styled.div({
  position: 'fixed',
  zIndex: 999,
  backgroundColor: style.colors.monochrome.white,
  top: '0',
  pointerEvents: 'all',
  overflow: 'scroll',
  height: '100vh',
  width: '100vw',
  overscrollBehaviorY: 'contain',
})

const ZoomImage = styled(PictureImage, {
  height: '100%',
  width: '100%',
  objectFit: 'cover',
  display: 'block',
})

const SwiperVideo = styled.div({
  height: 'calc(4/3 * 100vw)',
  width: '100vw',
  objectFit: 'cover',
})

// const SwiperImage = styled.img({
//   height: '100%',
//   width: '100%',
//   objectFit: 'cover',
// })

const SwiperImage = styled(PictureImage, {
  height: '100%',
  width: '100%',
  objectFit: 'cover',
})


const TagsOverlay = styled(OverlayNotABackdrop, {
  zIndex: 1,
  pointerEvents: 'none',
  height: '100%',
  width: '100%',
  display: 'flex',
  padding: { xy: '12px' },
  alignItems: 'flex-end',
  gap: '8px',
  flexDirection: 'column',
  justifyContent: 'flex-end',
})

const ShowZoomedWrap = styled(OverlayNotABackdrop, {
  zIndex: 1,
  pointerEvents: 'none',
  height: '100%',
  width: '100%',
  display: 'flex',
  padding: { xy: '12px' },
  alignItems: 'flex-end',
  gap: '8px',
  flexDirection: 'column',
  justifyContent: 'flex-start',
})

const ShowZoomedIcon = styled(Plus24x24, {
  pointerEvents: 'all',
  cursor: 'pointer',
})

const FavoriteWrap = styled(OverlayNotABackdrop, {
  zIndex: 1,
  pointerEvents: 'none',
  height: '100%',
  width: '100%',
  display: 'flex',
  padding: { xy: '16px' },
  alignItems: 'flex-start',
  gap: '8px',
  flexDirection: 'column',
  justifyContent: 'space-between',
})

export const MobileImageGallery = (props: {
  resources: Resource[];
  tags?: TagViewModel[];
  isFavorited?: boolean;
  toggleFavorited?: (e: any) => Promise<void>;
}) => {
  const { resources, tags, isFavorited, toggleFavorited } = props;
  const [currentItem, setCurrentItem] = useState(0);
  const [loadedImages, setLoadedImages] = useState(0);
  const [showZoom, setShowZoom] = useState(false);
  const swiperRef = useRef<any>(null);
  let zoomContainerRef = useRef<HTMLDivElement>(null);
  let zoomImageContainerRef = useRef<HTMLDivElement>(null);
  const zoomImageRefs = useRef([])
  const itemCount = resources?.length ?? 0;

  useEffect(() => {
    let isMounted = true;
    if (itemCount === 0 || !showZoom) {
      return () => { }
    }

    if (loadedImages === itemCount && isMounted) {
      zoomImageRefs.current[currentItem].scrollIntoView({
        behavior: "smooth",
        block: "start",
        inline: "nearest"
      })
      return () => setLoadedImages(() => 0)
    }
    return () => { isMounted = false; }
  }, [loadedImages, showZoom]);

  return (
    <Wrap>
      {showZoom && (
        <>
          <ShowZoomWrap>
            <ShowZoomIcon
              onClick={() => {
                setShowZoom(false);
              }}
            />
          </ShowZoomWrap>
          <ZoomContainer
            ref={zoomContainerRef}
          >
            <styled.Div ref={zoomImageContainerRef}>
              {resources.map((item, index) => {
                return (
                  <ZoomImage
                    key={index}
                    src={item.imageUrl}
                    preset={Preset.ItemZoom}
                    onLoad={() => {
                      setLoadedImages(() => loadedImages + 1)
                    }}
                    imageRef={(elm) => zoomImageRefs.current[index] = elm}
                  />
                )
              })}
            </styled.Div>
          </ZoomContainer>
        </>
      )}
      <SwiperList
        slidesPerView={1}
        spaceBetween={0}
        isCompact={true}
        showNav={false}
        sliderRef={swiperRef}
        onIndexChange={() => setCurrentItem(swiperRef.current.activeIndex)}
        pagination={itemCount > 0}
        pagingItemCount={itemCount}
        freeMode={false}
        overflowHidden={true}
        paginationContainerCss={{
          position: 'absolute',
          bottom: '16px',
          left: '16px',
          zIndex: 1,
        }}
        cssMode={true}
      >
        {resources.map((item, index) => (
          <div key={index} className="swiper-slide">
            {item.type === 'VIDEO' ? (
              <SwiperVideo>
                <Video
                  videoSources={[
                    {
                      url: item.videoUrlSmallScreen !== null ? item.videoUrlSmallScreen : item.videoUrl,
                      type: 'MP4',
                      device: 'ALL',
                    } as VideoSource,
                  ]}
                  previewImage={{ url: item.imageUrl }}
                  isCompact={false}
                  videoStarted={true}
                  loop
                ></Video>
              </SwiperVideo>
            ) : (
              <SwiperImage src={item.imageUrl} preset={Preset.Item} />
            )}
          </div>
        ))}
      </SwiperList>
      <TagsOverlay>
        {tags &&
          tags.slice(0, 2).map((tag, index) => (
            <BgTag
              key={index}
              css={{ backgroundColor: style.colors.monochrome.white, color: colorFromTag(tag.code, tag.color) }}
            >
              {tag.tag}
            </BgTag>
          ))}
      </TagsOverlay>
      <ShowZoomedWrap>
        <ShowZoomedIcon
          onClick={() => {
            setShowZoom(true);
          }}
        />
      </ShowZoomedWrap>
      <FavoriteWrap>
        {!!toggleFavorited && (
          <styled.Div css={{ pointerEvents: 'all', cursor: 'pointer' }} onClick={toggleFavorited}>
            {isFavorited ? (
              <FavoriteFullIcon />
            ) : (
              <FavoriteIcon />
            )}
          </styled.Div>
        )}
      </FavoriteWrap>
    </Wrap>
  );
};
